<template>
  <div v-if="productSeen.length">
    <AkHeading2
      class="mx-auto ds-flex ds-font-bold ds-text-2xl ds-mb-4"
      data-testid="history-product-title"
    >
      {{ t('@@core_discovery.products-history.title') }}
    </AkHeading2>
    <AkCarousel
      :per-page="5"
      :per-move="1"
      :gap-size="16"
      :padding-left="1"
      :padding-right="50"
      :arrows="true"
      controller-size="xl"
      justify="start"
      :pagination="false"
      :breakpoints="breakpoints"
    >
      <AkCarouselItem
        v-for="(hit, index) in productSeen"
        :key="index"
      >
        <CardProductHistory
          :product="hit"
          :page-number="1"
          :position="index"
          :can-get-wholesale-price="canGetWholesalePrice"
          :is-user-authenticated="isUserAuthenticated"
          @clicked="productSnippetTracking.trackProductClicked({ product: hit })"
          @brand-clicked="productSnippetTracking.trackProductBrandClicked({ product: hit })"
          @add-to-cart-clicked="productSnippetTracking.trackQuickAddToCartClicked({ product: hit })"
          @added-to-cart="
            (addToCartProps) =>
              productSnippetTracking.trackVariantAddedToCart({
                productProperty: createProductProperty(hit),
                brandProperty: createBrandProperty(hit.brand),
                quantity: addToCartProps.quantity,
              })
          "
          @like-clicked="productSnippetTracking.trackProductLiked({ product: hit, position: index, id_product: hit.id })"
          @unlike-clicked="productSnippetTracking.trackProductDisliked({ product: hit })"
        />
      </AkCarouselItem>
    </AkCarousel>
  </div>
</template>

<script setup lang="ts">
import { provide, ref, watchEffect } from 'vue';
import { AkCarousel, AkCarouselItem, AkHeading2 } from '@ankorstore/design-system';
import { getProductsHistory } from '../services/product-history';
import { CardProductHistory } from '@bc/discovery/feature/cards';
import { useI18n } from 'vue-i18n';
import RootStoreHelpers from '@monolith/legacy/store/helpers';
import { getProductSnippetTracking } from '@client/modules/discovery/domain/tracking';
import { getSectionId } from '@bc/shared';
import { createProductProperty } from '@client/monolith/legacy/services/analytics/properties/product-property';
import { createBrandProperty } from '@client/monolith/legacy/services/analytics/properties/brand-property';
import { isEnabled } from '@client/monolith/legacy/services/features';
import { useFundedDiscounts, FUNDED_DISCOUNTS_KEY } from '@bc/loyalty';

const { canGetWholesalePrice, isUserAuthenticated } = RootStoreHelpers.useGetters([
  'canGetWholesalePrice',
  'isUserAuthenticated',
]);

const sectionId = getSectionId({ sectionType: 'reco_carousel', valueProposition: 'productHistoryViewed' });

const productSnippetTracking = getProductSnippetTracking(ref(sectionId), ref(undefined), ref('ProductsHistoryViewed'));

const { t } = useI18n();

const productSeen = ref([]);

// Provide funded discounts to all child CardProduct instances (one API call per carousel instead of N)
const { ensureLoaded: ensureFundedDiscountsLoaded, getOffer, hasOffer } = useFundedDiscounts();
provide(FUNDED_DISCOUNTS_KEY, { getOffer, hasOffer });

const breakpoints = {
  1440: { perPage: 5, arrows: true },
  1280: { perPage: 4, arrows: true },
  960: { perPage: 3, arrows: false },
  640: { perPage: 2, arrows: false },
  480: { perPage: 1, arrows: false },
};
watchEffect(async () => {
  if (isUserAuthenticated.value && isEnabled('RET-3579')) {
    productSeen.value = (await getProductsHistory()) ?? [];

    // Batch-load funded discounts for all products in the carousel
    const brandUuids = [...new Set(
      (productSeen.value || [])
        .map((item) => item.brand?.uuid)
        .filter(Boolean)
    )] as string[];
    if (brandUuids.length > 0) {
      ensureFundedDiscountsLoaded(brandUuids).catch(() => {});
    }
  }
});
</script>
